import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";
import {
  LayoutDashboard, Users, Tractor, Wrench, PackageSearch,
  FileText, Wallet, BarChart3, LogOut, Menu, X, ShieldCheck, PackagePlus, ShoppingCart, ClipboardList, Receipt, Settings, CalendarDays, TrendingDown
} from "lucide-react";
import { useState } from "react";
import NotificationBell from "./NotificationBell";
import ForcePasswordChangeModal from "./ForcePasswordChangeModal";

const ALL_ITEMS = [
  { to: "/", label: "Pano", icon: LayoutDashboard, end: true, test: "dashboard", perm: "dashboard" },
  { to: "/traktorler", label: "Traktörler", icon: Tractor, test: "tractors", perm: "tractors" },
  { to: "/servis", label: "Servis", icon: Wrench, test: "service", perm: "service" },
  { to: "/servis-talepleri", label: "Servis Talepleri", icon: ClipboardList, test: "service-requests", perm: "service_requests" },
  { to: "/parca-talepleri", label: "Parça Talepleri", icon: PackagePlus, test: "part-requests", perm: "part_requests" },
  { to: "/parca-siparisleri", label: "Parça Siparişleri", icon: ShoppingCart, test: "part-orders", perm: "part_orders" },
  { to: "/yedek-parca", label: "Yedek Parça", icon: PackageSearch, test: "parts", perm: "parts" },
  { to: "/musteriler", label: "Müşteriler", icon: Users, test: "customers", perm: "customers" },
  { to: "/teklifler", label: "Teklifler", icon: FileText, test: "quotes", perm: "quotations" },
  { to: "/senetler", label: "Senet & Çek", icon: Receipt, test: "notes", perm: "notes" },
  { to: "/senet-takvimi", label: "Taksit Takvimi", icon: CalendarDays, test: "notes-calendar", perm: "notes" },
  { to: "/finans", label: "Finans", icon: Wallet, test: "finance", perm: "finance" },
  { to: "/giderler", label: "Giderler", icon: TrendingDown, test: "expenses", anyPerm: ["expenses_create", "expenses_view"] },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3, test: "reports", perm: "reports" },
  { to: "/kullanicilar", label: "Kullanıcılar", icon: ShieldCheck, test: "users", perm: "users" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, test: "settings", perm: "settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [openMobile, setOpenMobile] = useState(false);
  const navigate = useNavigate();

  const perms = user?.permissions || [];
  const items = ALL_ITEMS.filter((it) => (it.anyPerm ? it.anyPerm.some((p) => perms.includes(p)) : perms.includes(it.perm)));
  const bottomNav = items.slice(0, 5);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <ForcePasswordChangeModal />
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-slate-200 bg-white sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-[#1B4D3E] text-white grid place-items-center font-bold">TP</div>
            <div>
              <div className="font-extrabold tracking-tight text-slate-900">TraktörPro</div>
              <div className="text-[11px] uppercase tracking-widest text-[#1B4D3E] font-bold">Bayi & Servis</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.test}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1B4D3E] text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <it.icon size={18} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="px-2 pb-2 text-xs text-slate-500">
            <div className="truncate font-semibold text-slate-800">{user?.full_name || user?.username}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#1B4D3E] font-bold">{user?.role || "kullanıcı"}</div>
          </div>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={logout} data-testid="logout-btn">
            <LogOut size={16} /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {openMobile && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpenMobile(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-[#1B4D3E] text-white grid place-items-center font-bold">TP</div>
                <div className="font-extrabold">TraktörPro</div>
              </div>
              <button onClick={() => setOpenMobile(false)} className="p-2"><X size={20} /></button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setOpenMobile(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium ${
                      isActive ? "bg-[#1B4D3E] text-white" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                >
                  <it.icon size={18} /> {it.label}
                </NavLink>
              ))}
            </nav>
            <Button variant="outline" onClick={logout} className="gap-2"><LogOut size={16} /> Çıkış Yap</Button>
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3">
            <div className="flex items-center gap-2">
              <button onClick={() => setOpenMobile(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="open-mobile-menu" aria-label="Menü">
                <Menu size={22} />
              </button>
              <div className="lg:hidden flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-[#1B4D3E] text-white grid place-items-center text-xs font-bold">TP</div>
                <span className="font-bold text-slate-900">TraktörPro</span>
              </div>
              <div className="hidden lg:block text-sm text-slate-500">Bayi ve Servis Yönetim Merkezi</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:block text-xs text-slate-500">
                <span className="font-semibold text-slate-800">{user?.full_name || user?.username}</span>
              </div>
              <NotificationBell />
              {perms.includes("service") && (
                <Button size="sm" className="bg-[#D97706] hover:bg-[#B45309] text-white hidden sm:inline-flex" onClick={() => navigate("/servis")} data-testid="header-quick-wo">
                  Yeni İş Emri
                </Button>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8 pb-24 lg:pb-8 w-full max-w-full overflow-x-hidden">
          <Outlet />
        </main>

        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 mobile-nav-shadow">
          <div className={`grid`} style={{ gridTemplateColumns: `repeat(${Math.max(bottomNav.length, 1)}, minmax(0,1fr))` }}>
            {bottomNav.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`bnav-${it.test}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] text-[11px] font-medium ${
                    isActive ? "text-[#1B4D3E]" : "text-slate-500"
                  }`
                }
              >
                <it.icon size={20} />
                <span className="truncate px-1">{it.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}
